import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { PipesModule } from '../../pipes';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorComponent } from '../../core';
import { APP_MODE, BusinessObject, Customer } from '../../models';
import { DialogService } from '../../services';
import {
  CustomerSelectionDialogComponent,
  CustomerSelectionDialogComponentData,
} from './customer-selection-dialog/customer-selection-dialog.component';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'lib-customer-selection-form',
  standalone: true,
  imports: [
    CommonModule,
    PipesModule,
    // material design
    MatIconModule,
  ],
  templateUrl: './customer-selection-form.component.html',
  styleUrl: './customer-selection-form.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomerSelectionFormComponent),
      multi: true,
    },
  ],
})
export class CustomerSelectionFormComponent
  extends ControlValueAccessorComponent
  implements ControlValueAccessor
{
  @Input() mode: APP_MODE;
  @Input() businessObject: BusinessObject;

  private _customers: Customer[];
  @Input() set customers(value: Customer[]) {
    this._customers = value;
    this.updateSelectedCustomer();
  }
  get customers(): Customer[] {
    return this._customers;
  }

  @Output() customersChange = new EventEmitter<Customer[]>();

  public isDisabled = false;

  public selectedCustomer: Customer;
  public selectedCustomerId: number;

  constructor(private dialogService: DialogService) {
    super();
  }

  public isAbleToSelectCustomer(): boolean {
    return !this.isDisabled && this.customers?.length > 0;
  }

  public openCustomerSelectionDialog(): void {
    if (!this.isAbleToSelectCustomer()) {
      return;
    }

    const dialogRef = this.dialogService.openDialog(
      CustomerSelectionDialogComponent,
      this.dialogService.createLargeDialogConfig({
        mode: this.mode,
        businessObject: this.businessObject,
        customers: this.customers,
        currentSelectedCustomerId: this.selectedCustomerId,
      } as CustomerSelectionDialogComponentData)
    );

    dialogRef
      .afterClosed()
      .pipe(filter((customer) => !!customer))
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((customer) => {
        // if the new customer is created => emit new list
        if (!this.customers?.find((c) => c.id === customer.id)) {
          this.customers = [...(this.customers || []), customer];
          this.customersChange.emit(this.customers);
        }

        this.writeValue(customer?.id);
        this.emitSelectedCustomerId();
      });
  }

  private emitSelectedCustomerId(): void {
    this._onChange(this.selectedCustomerId);
  }

  private updateSelectedCustomer(): void {
    this.selectedCustomer = this.customers?.find(
      (c) => c.id === this.selectedCustomerId
    );
  }

  // required for NG_VALUE_ACCESSOR
  writeValue(customerId: number) {
    this.selectedCustomerId = customerId;
    this.updateSelectedCustomer();
  }

  // required for NG_VALUE_ACCESSOR
  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }
}
